import type { Message } from "@/dtos/Unipile.dto";

/**
 * Platform-specific message formatting utilities
 */

export type Platform = "whatsapp" | "email" | "linkedin" | "sms";

/**
 * Get platform-specific message bubble class
 */
export function getMessageBubbleClass(message: Message, platform: Platform): string {
  const baseClass = "message-bubble";
  const directionClass = message.isOwn ? "outgoing" : "incoming";
  return `${baseClass} ${baseClass}--${platform} ${baseClass}--${directionClass}`;
}

/**
 * Get platform-specific colors
 */
export function getPlatformColors(platform: Platform): {
  outgoing: string;
  incoming: string;
  outgoingText: string;
  incomingText: string;
  accent: string;
} {
  switch (platform) {
    case "whatsapp":
      return {
        outgoing: "#e9fbe4",
        incoming: "#f7f7f7",
        outgoingText: "#111b21",
        incomingText: "#111b21",
        accent: "#25d366",
      };
    case "linkedin":
      return {
        outgoing: "#eff6ff",
        incoming: "#f9fafb",
        outgoingText: "#111827",
        incomingText: "#111827",
        accent: "#0077b5",
      };
    case "sms":
      return {
        outgoing: "#eff6ff",
        incoming: "#f9fafb",
        outgoingText: "#111827",
        incomingText: "#111827",
        accent: "#3b82f6",
      };
    default:
      return {
        outgoing: "#7c3aed",
        incoming: "#f3f4f6",
        outgoingText: "#ffffff",
        incomingText: "#111827",
        accent: "#7c3aed",
      };
  }
}

/**
 * Check if message should show tail (chat bubble tail)
 */
export function shouldShowTail(platform: Platform): boolean {
  return platform === "whatsapp";
}

/**
 * Format message content for display
 */
export function formatMessageContent(content: string, platform: Platform): string {
  // Basic HTML escaping if needed
  if (!content) return "";

  // For platforms that support HTML, return as is
  if (platform === "email") {
    return content;
  }

  // For chat platforms, convert basic markdown-like formatting
  // Convert newlines to <br>
  let formatted = content.replace(/\n/g, "<br>");

  // Convert URLs to links
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  formatted = formatted.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  return formatted;
}

/**
 * Get message alignment based on platform and direction
 */
export function getMessageAlignment(
  message: Message,
  platform: Platform
): "left" | "right" | "center" {
  if (message.isOwn) {
    return "right";
  }
  return "left";
}

/**
 * Get maximum message width for platform
 */
export function getMaxMessageWidth(platform: Platform): string {
  switch (platform) {
    case "whatsapp":
      return "75%";
    case "linkedin":
      return "70%";
    case "sms":
      return "75%";
    default:
      return "75%";
  }
}

interface PreviewParticipant {
  providerUserId: string;
  name?: string | null;
  is_self?: boolean;
}

interface PreviewConversation {
  lastMessage?: string | null;
  participants?: PreviewParticipant[];
  contactName?: string;
  title?: string;
}

// Backend sends unresolved reaction placeholders as "{{<providerUserId>}} reacted 👍" in
// lastMessage. Requiring the "reacted" suffix (instead of matching any {{...}} in the string)
// avoids mangling a message that happens to contain literal double curly braces; the /g flag
// keeps resolving every such notice if more than one gets concatenated into lastMessage.
const REACTION_PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}(?=\s+reacted\b)/g;

/**
 * Resolve the raw provider-id placeholder in a conversation's lastMessage reaction
 * notice to the reacting participant's display name.
 */
export function resolveLastMessagePreview(conversation: PreviewConversation): string {
  const text = conversation.lastMessage;
  if (!text) return text || "";

  return text.replace(REACTION_PLACEHOLDER_REGEX, (_match, providerUserId: string) => {
    const participant = conversation.participants?.find((p) => p.providerUserId === providerUserId);
    if (participant?.is_self) return "You";
    return participant?.name || conversation.contactName || conversation.title || "Someone";
  });
}
